import { useEffect, useLayoutEffect, useRef } from 'react';
import { renderAsync } from 'docx-preview';
import styled from 'styled-components';

import { COLORS } from '@/shared/constants/colors';
import { BFC } from '@/shared/types';

type DocxDocumentProps = {
  file: File | string;
  onRendered?: () => void;
};

export const DocxDocument: BFC<DocxDocumentProps> = ({ file, onRendered }) => {
  const ref = useRef<HTMLDivElement>(null);

  const rescale = () => {
    if (!ref.current) return;
    // reset
    ref.current.style.transform = `scale(1)`;
    const pageWidth = ref.current.querySelector('.docx');
    if (!pageWidth) return;

    const width = pageWidth.getBoundingClientRect().width;
    const parentWidth = ref.current.parentElement?.getBoundingClientRect().width;
    if (!parentWidth) return;

    const scale = parentWidth / width;
    ref.current.style.transform = `scale(${scale})`;
  };

  const renderDoc = async () => {
    if (!ref.current) return;
    let arrayBuffer: ArrayBuffer;
    if (typeof file === 'string') {
      const resp = await fetch(file);
      if (!resp.ok) throw new Error(`Failed to fetch the file`);
      arrayBuffer = await resp.arrayBuffer();
    } else {
      arrayBuffer = await file.arrayBuffer();
    }

    await renderAsync(arrayBuffer, ref.current, undefined, {
      inWrapper: true,
    });
    rescale();
    setTimeout(() => {
      onRendered?.();
    }, 0);
  };

  useEffect(() => {
    if (!ref.current) return;
    renderDoc();
  }, [ref]);

  useLayoutEffect(() => {
    addEventListener('resize', rescale);
    return () => {
      removeEventListener('resize', rescale);
    };
  }, [ref]);

  return <Wrapper ref={ref}></Wrapper>;
};

const Wrapper = styled.div`
  max-height: 100%;
  width: 100%;
  transform-origin: top center;

  .docx-wrapper {
    width: 100%;
    background: ${COLORS.colorFilePreviewBg};
  }
`;
